/**
 * Deep merges a base configuration with stage-specific overrides.
 * Arrays are replaced entirely (not merged).
 */
export function mergeConfig<T extends Record<string, unknown>>(base: T, override?: Partial<T>): T {
  if (!override) return base;

  const result = { ...base };

  for (const key in override) {
    const overrideValue = override[key];

    if (overrideValue === undefined) continue;

    if (Array.isArray(overrideValue)) {
      // Arrays replace entirely
      result[key] = overrideValue as T[Extract<keyof T, string>];
    } else if (typeof overrideValue === 'object' && overrideValue !== null) {
      const baseValue = base[key];

      // Nested objects merge recursively
      result[key] = mergeConfig(
        typeof baseValue === 'object' && baseValue && !Array.isArray(baseValue) ? baseValue : {},
        overrideValue,
      ) as T[Extract<keyof T, string>];
    } else {
      // Primitives replace
      result[key] = overrideValue as T[Extract<keyof T, string>];
    }
  }

  return result;
}
